import { useCallback, useState } from 'react'

import { CAMPAIGN_CHANNELS } from '#/entities/campaign'
import type { CampaignChannel } from '#/entities/campaign'
import { StepCondition } from '#/shared/api/generated/communication/models'

export const CAMPAIGN_STEP_DELAYS = {
  immediately: 'immediately',
  oneDay: 'oneDay',
  twoDays: 'twoDays',
  threeDays: 'threeDays',
  sevenDays: 'sevenDays',
  custom: 'custom',
} as const

export type CampaignStepDelay =
  (typeof CAMPAIGN_STEP_DELAYS)[keyof typeof CAMPAIGN_STEP_DELAYS]

export const CAMPAIGN_STEP_DELAY_ORDER: readonly CampaignStepDelay[] = [
  CAMPAIGN_STEP_DELAYS.immediately,
  CAMPAIGN_STEP_DELAYS.oneDay,
  CAMPAIGN_STEP_DELAYS.twoDays,
  CAMPAIGN_STEP_DELAYS.threeDays,
  CAMPAIGN_STEP_DELAYS.sevenDays,
  CAMPAIGN_STEP_DELAYS.custom,
]

export const CAMPAIGN_STEP_CONDITIONS = {
  noReply: 'noReply',
  noClick: 'noClick',
  noBooking: 'noBooking',
} as const

export type CampaignStepCondition =
  (typeof CAMPAIGN_STEP_CONDITIONS)[keyof typeof CAMPAIGN_STEP_CONDITIONS]

export const CAMPAIGN_STEP_CONDITION_ORDER: readonly CampaignStepCondition[] = [
  CAMPAIGN_STEP_CONDITIONS.noReply,
  CAMPAIGN_STEP_CONDITIONS.noClick,
  CAMPAIGN_STEP_CONDITIONS.noBooking,
]

/** Picker delays in the service's unit. `custom` is typed in days, so it has no fixed value. */
export const DELAY_MINUTES: Record<CampaignStepDelay, number | null> = {
  [CAMPAIGN_STEP_DELAYS.immediately]: 0,
  [CAMPAIGN_STEP_DELAYS.oneDay]: 1_440,
  [CAMPAIGN_STEP_DELAYS.twoDays]: 2_880,
  [CAMPAIGN_STEP_DELAYS.threeDays]: 4_320,
  [CAMPAIGN_STEP_DELAYS.sevenDays]: 10_080,
  [CAMPAIGN_STEP_DELAYS.custom]: null,
}

export const MINUTES_PER_DAY = 1_440
const MAX_CUSTOM_DAYS = 365

/** A typed day count in the service's unit, or null while it is not a whole 1–365. */
export function customDaysToMinutes(days: string): number | null {
  if (!/^\d+$/.test(days)) return null
  const count = Number(days)
  if (count < 1 || count > MAX_CUSTOM_DAYS) return null
  return count * MINUTES_PER_DAY
}

/** The picker's delay in minutes — a preset's own value, or the typed custom days. */
export function resolveDelayMinutes(
  delay: CampaignStepDelay,
  customDays: string,
): number | null {
  return delay === CAMPAIGN_STEP_DELAYS.custom
    ? customDaysToMinutes(customDays)
    : DELAY_MINUTES[delay]
}

/** One choice on the edit composer's wait row, in the service's unit. */
export interface CampaignStepWaitOption {
  minutes: number
  /** The picker's own preset, or null for a stored wait no preset matches. */
  preset: CampaignStepDelay | null
}

/**
 * The wait row an edit opens on: the picker's presets, plus the step's stored
 * wait when none of them matches it — a step created with five days must still
 * read as five days, and saving without touching the row must keep it.
 */
export function campaignStepWaitOptions(
  stored: number,
): CampaignStepWaitOption[] {
  const presets = CAMPAIGN_STEP_DELAY_ORDER.flatMap((preset) => {
    const minutes = DELAY_MINUTES[preset]
    return minutes == null ? [] : [{ minutes, preset }]
  })
  if (presets.some((option) => option.minutes === stored)) return presets
  return [...presets, { minutes: stored, preset: null }].sort(
    (a, b) => a.minutes - b.minutes,
  )
}

export const CONDITION_TO_API: Record<CampaignStepCondition, StepCondition> = {
  [CAMPAIGN_STEP_CONDITIONS.noReply]: StepCondition.no_reply,
  [CAMPAIGN_STEP_CONDITIONS.noClick]: StepCondition.no_click,
  [CAMPAIGN_STEP_CONDITIONS.noBooking]: StepCondition.no_booking,
}

/** A stored `only_if` in the picker's vocabulary; null or empty reads as "always send". */
export function conditionsFromApi(
  onlyIf: readonly StepCondition[] | null | undefined,
): CampaignStepCondition[] {
  return CAMPAIGN_STEP_CONDITION_ORDER.filter((condition) =>
    onlyIf?.includes(CONDITION_TO_API[condition]),
  )
}

/**
 * The add-step picker's own state.
 *
 * "Always send" is not a fourth condition — it is the empty set of them, so
 * picking it clears the others and picking any of the others clears it. Keeping
 * that as one derived flag rather than a separate toggle is what stops the two
 * from disagreeing.
 */
export const CAMPAIGN_ADD_STEP_PHASES = {
  closed: 'closed',
  picking: 'picking',
  composing: 'composing',
} as const

export type CampaignAddStepPhase =
  (typeof CAMPAIGN_ADD_STEP_PHASES)[keyof typeof CAMPAIGN_ADD_STEP_PHASES]

export function useCampaignAddStep() {
  const [phase, setPhase] = useState<CampaignAddStepPhase>(
    CAMPAIGN_ADD_STEP_PHASES.closed,
  )
  const [channel, setChannel] = useState<CampaignChannel>(CAMPAIGN_CHANNELS.sms)
  const [delay, setDelay] = useState<CampaignStepDelay>(
    CAMPAIGN_STEP_DELAYS.twoDays,
  )
  const [customDays, setCustomDays] = useState('')
  const [conditions, setConditions] = useState<CampaignStepCondition[]>([
    ...CAMPAIGN_STEP_CONDITION_ORDER,
  ])
  const delayMinutes = resolveDelayMinutes(delay, customDays)

  const toggleCondition = useCallback((condition: CampaignStepCondition) => {
    setConditions((current) =>
      current.includes(condition)
        ? current.filter((item) => item !== condition)
        : [...current, condition],
    )
  }, [])

  const setAlwaysSend = useCallback(() => setConditions([]), [])

  const close = useCallback(() => setPhase(CAMPAIGN_ADD_STEP_PHASES.closed), [])

  return {
    phase,
    onOpen: () => setPhase(CAMPAIGN_ADD_STEP_PHASES.picking),
    /** The picker is settled — go write the message. */
    onCompose: () => setPhase(CAMPAIGN_ADD_STEP_PHASES.composing),
    /** Back out of the composer to the picker rather than all the way out. */
    onBackToPicker: () => setPhase(CAMPAIGN_ADD_STEP_PHASES.picking),
    onClose: close,
    channel,
    onChannelChange: setChannel,
    delay,
    onDelayChange: setDelay,
    customDays,
    onCustomDaysChange: setCustomDays,
    /** Null only while Custom is picked with no valid day count typed. */
    delayMinutes,
    conditions,
    toggleCondition,
    alwaysSend: conditions.length === 0,
    setAlwaysSend,
  }
}

export type UseCampaignAddStepResult = ReturnType<typeof useCampaignAddStep>
